import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { searchPlaces } from '@/lib/geo/geocode'

/** Debounce a fast-changing value (e.g. a search box) by `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debounced
}

/**
 * Debounced + cached US place search. Cuts request volume (Nominatim asks for ≤1/s)
 * via a 400ms debounce and TanStack Query's per-term cache.
 */
export function useGeocodeSearch(query: string) {
  const term = useDebounced(query.trim(), 400)
  const q = useQuery({
    queryKey: ['geocode', term.toLowerCase()],
    queryFn: ({ signal }) => searchPlaces(term, signal),
    enabled: term.length >= 2,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  })
  return { ...q, term }
}
